'use client';

import { useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import { IconMinus, IconPlus } from '@/components/icons';
import { resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import type { BlockImageDto, BlockShowcaseItem } from '@/lib/sanity/dto';
import { HALF_WIDTH_HINT, imageSizes } from '@/lib/utils/imageSizes';

export type FeatureShowcaseGalleryProps = {
    items?: BlockShowcaseItem[];
    /** Standard-Vorschau, sichtbar solange keine Karte aktiv ist. */
    image?: BlockImageDto;
    previewSide?: 'left' | 'right';
    iconBg?: 'surface-2' | 'surface-3';
    /** Section-Hintergrund – färbt das Overlay über dem Sticky-Bild. */
    background?: 'page' | 'surface';
};

// The preview fills one half of the viewport from `lg` up.
const PREVIEW_SIZES = imageSizes(HALF_WIDTH_HINT);
const DEFAULT_LAYER = '__default';

/** Viewport-Breite (< lg), ab der die Karten als Klick-Akkordeon statt
 *  Hover-Vorschau laufen. Muss zu den `max-lg:`-Klassen der Karten passen. */
function isAccordionViewport() {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
}

/** Interaktive Feature-Karten: auf Desktop wechselt die Vorschau bei Hover;
 *  unter `lg` werden die Karten zu einem +/- -Akkordeon und die Vorschau
 *  wandert auf sehr schmalen Viewports (< 820px) als Sticky-Bild nach oben. */
export function FeatureShowcaseGallery({ items, image, previewSide, iconBg, background }: FeatureShowcaseGalleryProps) {
    const previewLeft = previewSide === 'left';
    const cards = (items ?? []).map((item, i) => ({ ...item, uid: item._key ?? `card-${i}` }));

    const [activeUid, setActiveUid] = useState<string | null>(null);
    const activeCard = cards.find((c) => c.uid === activeUid);
    const activeLayer = activeCard?.image?.ref ? activeCard.uid : DEFAULT_LAYER;

    const toggle = (uid: string) => setActiveUid((prev) => (prev === uid ? null : uid));

    return (
        <div className="grid grid-cols-1 items-stretch gap-8 min-[820px]:grid-cols-2">
            <div className={cn('grid grid-cols-1 gap-3 lg:grid-cols-2', previewLeft && 'min-[820px]:order-2')}>
                {cards.map((item) => {
                    const isActive = activeUid === item.uid;
                    return (
                        <div
                            key={item.uid}
                            role="button"
                            tabIndex={0}
                            aria-expanded={isActive}
                            onMouseEnter={() => {
                                if (isAccordionViewport()) return;
                                setActiveUid(item.uid);
                            }}
                            onMouseLeave={() => {
                                if (isAccordionViewport()) return;
                                setActiveUid(null);
                            }}
                            onClick={() => {
                                if (!isAccordionViewport()) return;
                                toggle(item.uid);
                            }}
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                e.preventDefault();
                                toggle(item.uid);
                            }}
                            className={cn(
                                'group flex cursor-pointer select-none flex-col rounded-xl border p-(--space-card-20) transition-colors',
                                'max-lg:flex-row max-lg:flex-wrap max-lg:items-center max-lg:gap-x-3 max-lg:p-[12px_16px]',
                                'hover:border-[var(--pg-item-hover-border)] hover:bg-[var(--pg-item-hover-bg)]',
                                isActive
                                    ? 'border-[var(--pg-item-hover-border)] bg-[var(--pg-item-hover-bg)]'
                                    : 'border-hairline',
                            )}
                        >
                            <div
                                className={cn(
                                    'mb-3 flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors max-lg:mb-0',
                                    'group-hover:bg-[var(--pg-item-hover-icon-bg)] group-hover:text-[var(--pg-icon-p-stroke)]',
                                    isActive
                                        ? 'bg-[var(--pg-item-hover-icon-bg)] text-[var(--pg-icon-p-stroke)]'
                                        : cn(
                                              'text-[var(--pg-icon-stroke)]',
                                              iconBg === 'surface-3' ? 'bg-surface-3' : 'bg-surface-2',
                                          ),
                                )}
                            >
                                {item.icon ? <UiIcon name={item.icon} size={15} /> : null}
                            </div>

                            {item.title ? (
                                <h3
                                    className={cn(
                                        'card-title transition-colors group-hover:text-primary-700 max-lg:flex-1',
                                        isActive ? 'text-primary-700' : 'text-title-2',
                                    )}
                                >
                                    {item.title}
                                </h3>
                            ) : null}

                            {/* Akkordeon-Indikator: nur unter lg sichtbar, +/- je nach Zustand */}
                            <span
                                aria-hidden
                                className={cn(
                                    'ml-auto hidden shrink-0 max-lg:flex',
                                    isActive ? 'text-primary-500' : 'text-[var(--pg-icon-stroke)]',
                                )}
                            >
                                {isActive ? <IconMinus className="size-4.5" /> : <IconPlus className="size-4.5" />}
                            </span>

                            {item.description ? (
                                <div
                                    className={cn(
                                        'min-w-0 max-lg:grid max-lg:basis-full max-lg:pl-12',
                                        'max-lg:transition-[grid-template-rows] max-lg:duration-300 max-lg:ease-out',
                                        isActive
                                            ? 'max-lg:mt-1 max-lg:mb-2 max-lg:grid-rows-[1fr]'
                                            : 'max-lg:grid-rows-[0fr]',
                                    )}
                                >
                                    <div className="max-lg:overflow-hidden">
                                        <p className="mt-1 text-[13px] leading-normal text-copy-muted max-lg:mt-0">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <div
                className={cn(
                    'relative min-h-100 rounded-2xl bg-surface-2',
                    // Sticky-Vorschau: klebt mit --space-gutter Abstand unter dem 64px-Header (h-16).
                    'max-[1023px]:sticky max-[1023px]:top-[calc(4rem+var(--space-gutter))] max-[1023px]:min-h-0',
                    'min-[820px]:max-[1023px]:h-full',
                    // w-full: sonst leitet der Browser die Breite aus aspect-ratio + max-height ab (Bild wird schmal).
                    'max-[819px]:order-first max-[819px]:z-5 max-[819px]:aspect-3/2 max-[819px]:max-h-90 max-[819px]:w-full',
                    previewLeft && 'min-[820px]:order-1',
                )}
            >
                {/* Deckt den Sticky-Abstand über dem Bild opak ab, damit durchscrollender
				    Inhalt nicht zwischen Header und Vorschau durchscheint. */}
                <div
                    aria-hidden
                    className={cn(
                        'pointer-events-none absolute inset-x-0 bottom-full hidden h-(--space-gutter) max-[1023px]:block',
                        background === 'surface' ? 'bg-surface' : 'bg-page',
                    )}
                />
                {image?.ref ? <PreviewLayer image={image} active={activeLayer === DEFAULT_LAYER} /> : null}
                {cards.map((item) =>
                    item.image?.ref ? (
                        <PreviewLayer key={item.uid} image={item.image} active={activeLayer === item.uid} />
                    ) : null,
                )}
            </div>
        </div>
    );
}

function PreviewLayer({ image, active }: { image: BlockImageDto; active: boolean }) {
    const src = sanityImage.showcase(image);
    if (!src) return null;
    return (
        <div
            className={cn(
                'absolute inset-0 overflow-hidden rounded-2xl transition-opacity duration-300 ease-out',
                active ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden={!active}
        >
            <Image src={src} alt={resolveImageAlt(image)} fill sizes={PREVIEW_SIZES} className="object-cover" />
        </div>
    );
}
